import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { Input } from '../../../../components/ui/input';
import { Label } from '../../../../components/ui/label';
import { Textarea } from '../../../../components/ui/textarea';
import { toast } from 'sonner';
import { DialogForm } from '../../../../shared/components/common';
import { useCreateEmailTemplate, useUpdateEmailTemplate } from '../../hooks/useEmailTemplates';
import { useApiError } from '../../../../shared/hooks/useApiError';
import type { EmailTemplate, EmailTemplateCreate } from '../../types/prospecting';
import { MergeFieldToolbar } from './MergeFieldToolbar';

interface EmailTemplateFormDialogProps {
  open: boolean;
  onClose: () => void;
  template?: EmailTemplate;
}

interface FormData {
  name: string;
  subject: string;
  body: string;
  category: string;
}

export function EmailTemplateFormDialog({ open, onClose, template }: EmailTemplateFormDialogProps) {
  const { t } = useTranslation(['pipeline', 'common']);
  const isEditing = !!template;
  const createMutation = useCreateEmailTemplate();
  const updateMutation = useUpdateEmailTemplate();
  const { handleMutationError } = useApiError({ showToast: false });

  const bodyRef = useRef<HTMLTextAreaElement | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    getValues,
    setError,
    formState: { errors },
  } = useForm<FormData>({
    defaultValues: {
      name: template?.name || '',
      subject: template?.subject || '',
      body: template?.body || '',
      category: template?.category || 'outreach',
    },
  });

  useEffect(() => {
    if (open) {
      reset({
        name: template?.name || '',
        subject: template?.subject || '',
        body: template?.body || '',
        category: template?.category || 'outreach',
      });
    }
  }, [open, template, reset]);

  const onSubmit = async (data: FormData) => {
    try {
      if (isEditing && template) {
        await updateMutation.mutateAsync({ id: template.id, data });
        toast.success(t('prospecting.templateUpdated'));
      } else {
        const createData: EmailTemplateCreate = {
          name: data.name,
          subject: data.subject,
          body: data.body,
          category: data.category,
        };
        await createMutation.mutateAsync(createData);
        toast.success(t('prospecting.templateCreated'));
      }
      onClose();
    } catch (error) {
      handleMutationError(error, setError);
    }
  };

  const handleInsertMergeField = (token: string) => {
    const textarea = bodyRef.current;
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const currentBody = getValues('body');
      const newBody = currentBody.substring(0, start) + token + currentBody.substring(end);
      setValue('body', newBody, { shouldDirty: true });
      // Restore cursor position after the inserted token
      requestAnimationFrame(() => {
        textarea.focus();
        textarea.setSelectionRange(start + token.length, start + token.length);
      });
    } else {
      // Fallback: append to end
      setValue('body', getValues('body') + token, { shouldDirty: true });
    }
  };

  const isPending = createMutation.isPending || updateMutation.isPending;

  return (
    <DialogForm
      open={open}
      onClose={onClose}
      title={isEditing ? t('prospecting.editTemplate') : t('prospecting.createTemplate')}
      submitLabel={isEditing ? t('common:app.save') : t('common:app.create')}
      onSubmit={handleSubmit(onSubmit)}
      isLoading={isPending}
    >
      <div className="space-y-4">
        <div>
          <Label htmlFor="name">{t('prospecting.templateName')}</Label>
          <Input
            id="name"
            {...register('name', { required: true })}
            className={errors.name ? 'border-destructive' : ''}
          />
        </div>
        <div>
          <Label htmlFor="subject">{t('prospecting.templateSubject')}</Label>
          <Input
            id="subject"
            {...register('subject', { required: true })}
            className={errors.subject ? 'border-destructive' : ''}
          />
        </div>
        <div>
          <div className="flex items-center justify-between mb-1">
            <Label htmlFor="body">{t('prospecting.templateBody')}</Label>
            <MergeFieldToolbar onInsert={handleInsertMergeField} />
          </div>
          <Textarea
            id="body"
            rows={8}
            {...register('body', { required: true })}
            ref={(el) => {
              bodyRef.current = el;
              register('body').ref(el);
            }}
            className={errors.body ? 'border-destructive' : ''}
          />
        </div>
        <div>
          <Label htmlFor="category">{t('prospecting.templateCategory')}</Label>
          <Input
            id="category"
            {...register('category')}
          />
        </div>
      </div>
    </DialogForm>
  );
}
